﻿import {getAddress} from "../../utils/getAddress";

const DUTCH_COUNTRY_CODE = 'NL';
const DUTCH_ZIPCODE_REGEX = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/i;
const STREET_FIELD_ROLE = 'street';
const CITY_FIELD_ROLE = 'city';
const CONFIRMATION_DIALOG_ID = 'account-data-change-confirmation';
const ACCOUNT_DATA_FIELD_NAME = 'AlsoUpdateAccountData';
export const BEFORE_SUBMIT_EVENT_NAME = 'sds:form:beforeSubmit';

const TRACKED_FIELDS = [
    'SelectedCountryCode',
    'Zipcode',
    'HouseNumber',
    'HouseNumberAddition',
    'Street',
    'City',
    'EmailAddress',
    'PhoneNumberWork',
];

const MESSAGE_ROLES = {
    'phone-only': 'message-phone-only',
    'email-only': 'message-email-only',
    both: 'message-email-and-phone',
};

const normalise = (value) => (value ?? '').toString().trim();

export const getSubmitterFromEvent = (event) => {
    const submitter = event?.detail?.submitter ?? event?.submitter;
    return submitter instanceof HTMLElement ? submitter : null;
};

export const clearValidationMessage = (form, input) => {
    if (!form || !input?.name || !window.jQuery) return;

    const $input = window.jQuery(input);
    const $message = window.jQuery(form).find(`[data-valmsg-for="${input.name}"]`);

    $input.removeClass('input-validation-error');
    $message
        .empty()
        .removeClass('field-validation-error')
        .addClass('field-validation-valid');
};

export const initChangePersonalDataShared = ({
                                                 formRole,
                                                 boundFlag,
                                                 dynamicCountryRequiredFields,
                                             }) => {
    const form = document.querySelector(`[data-role="${formRole}"]`);
    if (!form) return;

    if (form.dataset[boundFlag] === 'true') return;
    form.dataset[boundFlag] = 'true';

    const countrySelect = form.querySelector('[data-role="country"]');
    const zipcodeInput = form.querySelector('[data-role="zipcode"]');
    const houseNumberInput = form.querySelector('[data-role="house-number"]');
    const streetInput = form.querySelector(`[data-role="${STREET_FIELD_ROLE}"]`);
    const cityInput = form.querySelector(`[data-role="${CITY_FIELD_ROLE}"]`);

    if (!zipcodeInput || !houseNumberInput) return;

    const readOriginalValues = () => {
        const node = form.querySelector('script[data-role="original-values"]');
        if (!node) return {};

        try {
            return JSON.parse(node.textContent || '{}');
        } catch {
            return {};
        }
    };

    const getFieldValue = (name) => {
        const selector = `input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`;
        const el = form.querySelector(selector);
        return el ? el.value : '';
    };

    const originalValues = readOriginalValues();

    const isFormDirty = () =>
        TRACKED_FIELDS.some(
            (name) => normalise(getFieldValue(name)) !== normalise(originalValues[name])
        );

    const resetSdsSubmittingState = () => {
        delete form.dataset.sdsSubmitting;
        form.classList.remove('is-submitting');
        form.setAttribute('aria-busy', 'false');

        form.querySelectorAll('button[type="submit"], input[type="submit"], [type="submit"]').forEach((el) => {
            el.classList.remove('loading');
        });

        form.querySelectorAll('[data-submitting="true"]').forEach((el) => {
            el.removeAttribute('data-submitting');
            el.removeAttribute('aria-disabled');

            if (el.tagName !== 'SDS-BUTTON') {
                el.disabled = false;
            }
        });
    };

    const clearValidationMessageForForm = (input) => clearValidationMessage(form, input);

    const setRequired = (input, required) => {
        if (!input) return;

        input.toggleAttribute('required', required);

        if (!window.jQuery?.validator) return;

        const $form = window.jQuery(form);
        const $input = window.jQuery(input);

        if (!$form.data('validator') || !$input.rules) return;

        if (required) {
            $input.rules('add', {
                required: true,
                messages: {
                    required: input.dataset.valRequired || 'Dit veld is verplicht.',
                },
            });
            return;
        }

        $input.rules('remove', 'required');
        clearValidationMessageForForm(input);
    };

    const setReadOnly = (input, readOnly) => {
        if (!input) return;

        input.readOnly = readOnly;
        input.toggleAttribute('aria-readonly', readOnly);
    };

    const isDutch = () =>
        (countrySelect?.value ?? DUTCH_COUNTRY_CODE).toUpperCase() === DUTCH_COUNTRY_CODE;

    const applyCountryMode = () => {
        const dutch = isDutch();

        setReadOnly(streetInput, dutch);
        setReadOnly(cityInput, dutch);

        if (!dynamicCountryRequiredFields) return;

        // Dutch flow validates zipcode + house number, while non-Dutch uses manual street + city.
        setRequired(zipcodeInput, dutch);
        setRequired(houseNumberInput, dutch);
        setRequired(streetInput, !dutch);
        setRequired(cityInput, !dutch);

        if (dutch) {
            clearValidationMessageForForm(streetInput);
            clearValidationMessageForForm(cityInput);
            return;
        }

        clearValidationMessageForForm(zipcodeInput);
        clearValidationMessageForForm(houseNumberInput);
    };

    let lookupDebounceTimer = null;
    let lookupAbortController = null;

    const clearAddressFields = () => {
        if (!streetInput || !cityInput) return;
        if (!streetInput.value && !cityInput.value) return;

        streetInput.value = '';
        cityInput.value = '';

        // Restore manual editability so the user can fill in the address themselves
        // until a successful lookup re-locks them.
        streetInput.readOnly = false;
        cityInput.readOnly = false;
        streetInput.removeAttribute('aria-readonly');
        cityInput.removeAttribute('aria-readonly');

        [streetInput, cityInput].forEach((input) => {
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    };

    const lookupAddress = () => {
        if (!isDutch()) return;

        const zipcode = zipcodeInput.value.trim();
        const houseNumber = houseNumberInput.value.trim();

        if (!zipcode || !houseNumber) {
            clearAddressFields();
            return;
        }

        if (!DUTCH_ZIPCODE_REGEX.test(zipcode.replace(/\s/g, ''))) return;

        // Cancel any in-flight request and pending debounce before starting a new lookup.
        clearTimeout(lookupDebounceTimer);
        lookupAbortController?.abort();
        lookupAbortController = new AbortController();

        const signal = lookupAbortController.signal;

        lookupDebounceTimer = setTimeout(() => {
            getAddress({
                zipcode,
                houseNumber,
                streetFieldRole: STREET_FIELD_ROLE,
                cityFieldRole: CITY_FIELD_ROLE,
                signal,
            });
        }, 300);
    };

    const handleBeforeSubmitLoadingState = (event) => {
        if (event.defaultPrevented) return;

        const submitter = getSubmitterFromEvent(event);
        if (submitter) {
            submitter.classList.add('loading');
        }
    };

    const handleBeforeSubmitPristineBlock = (event) => {
        if (event.defaultPrevented) return;

        if (!isFormDirty()) {
            event.preventDefault();
            resetSdsSubmittingState();
        }
    };

    const initAccountDataChangeConfirmation = () => {
        const sourceDialog = document.getElementById(CONFIRMATION_DIALOG_ID);
        if (!sourceDialog || !window.dialog) return;

        const setScenarioVisibility = (scenario) => {
            Object.values(MESSAGE_ROLES).forEach((role) => {
                const el = sourceDialog.querySelector(`[data-role="${role}"]`);
                if (el) {
                    el.hidden = true;
                }
            });

            const targetRole = MESSAGE_ROLES[scenario];
            if (!targetRole) return;

            const el = sourceDialog.querySelector(`[data-role="${targetRole}"]`);
            if (el) {
                el.hidden = false;
            }
        };

        const handleBeforeSubmitAccountDataChangeConfirmation = (event) => {
            if (event.defaultPrevented) return;

            const submitter = getSubmitterFromEvent(event);
            if (submitter?.name === ACCOUNT_DATA_FIELD_NAME) return;

            const emailChanged =
                normalise(getFieldValue('EmailAddress')) !== normalise(originalValues.EmailAddress);
            const phoneChanged =
                normalise(getFieldValue('PhoneNumberWork')) !== normalise(originalValues.PhoneNumberWork);

            if (!emailChanged && !phoneChanged) return;

            event.preventDefault();
            resetSdsSubmittingState();

            const scenario = emailChanged && phoneChanged
                ? 'both'
                : emailChanged
                    ? 'email-only'
                    : 'phone-only';

            setScenarioVisibility(scenario);

            window.dialog.open({
                elemId: CONFIRMATION_DIALOG_ID,
                dismissable: false,
            });
        };

        form.addEventListener(BEFORE_SUBMIT_EVENT_NAME, handleBeforeSubmitAccountDataChangeConfirmation);
    };

    const handleFormValidated = (event) => {
        if (event.detail?.valid === false) {
            resetSdsSubmittingState();
        }
    };

    const handleCountryChange = () => {
        applyCountryMode();
        lookupAddress();
    };

    form.addEventListener(BEFORE_SUBMIT_EVENT_NAME, handleBeforeSubmitLoadingState);
    form.addEventListener(BEFORE_SUBMIT_EVENT_NAME, handleBeforeSubmitPristineBlock);
    form.addEventListener('sds:form:validated', handleFormValidated);

    countrySelect?.addEventListener('change', handleCountryChange);
    zipcodeInput.addEventListener('input', lookupAddress);
    houseNumberInput.addEventListener('input', lookupAddress);

    applyCountryMode();
    initAccountDataChangeConfirmation();
};

export const initChangePersonalData = () => {
    initChangePersonalDataShared({
        formRole: 'change-personal-data-form',
        boundFlag: 'changePersonalDataBound',
        dynamicCountryRequiredFields: false,
    });
};

export const initChangePersonalDataStp = () => {
    initChangePersonalDataShared({
        formRole: 'change-personal-data-stp-form',
        boundFlag: 'changePersonalDataStpBound',
        dynamicCountryRequiredFields: true,
    });
};